import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Siren, Gift, Clock, QrCode, AlertTriangle, Filter, Key, Car, Dog, Baby, Smartphone, Package, Heart } from 'lucide-react';
import { useAuth, API } from '../App';
import { categoryLabel, allCategories, t } from '../i18n';
import Header from '../components/Header';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const urgentIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  className: 'urgent-marker'
});


const categoryIcons = {
  keys: Key, car: Car, pet: Dog, child: Baby, phone: Smartphone, bag: Package, doudou: Heart, other: Package,
};

const LostObjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('map'); // 'map' or 'list'

  const fetchLostItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lost-objects`);
      setItems(res.data.items || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLostItems(); }, [fetchLostItems]);

  const filteredItems = filter === 'all' ? items : items.filter(i => i.category === filter);

  // Items with location data for the map
  const mappableItems = filteredItems.filter(i => i.last_scan_location?.latitude && i.last_scan_location?.longitude);

  // Map center: France by default, or center on first item
  const mapCenter = mappableItems.length > 0
    ? [mappableItems[0].last_scan_location.latitude, mappableItems[0].last_scan_location.longitude]
    : [46.603354, 1.888334]; // France center

  const timeSince = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const hours = Math.floor((now - d) / (1000 * 60 * 60));
    if (hours < 1) return 'Il y a moins d\'1 heure';
    if (hours < 24) return `Il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Il y a ${days}j`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="lost-objects-page">
      <Header />

      {/* Hero Banner */}
      <div className="bg-danger text-white border-b-2 border-charcoal px-6 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Siren className="w-8 h-8 animate-pulse" />
              <h1 className="font-heading font-bold text-2xl sm:text-3xl">{t('lost.title')}</h1>
            </div>
            <p className="text-white/80 text-sm sm:text-base">{t('lost.subtitle')}</p>
          </div>
          {user && (
            <Link
              to="/dashboard"
              data-testid="declare-lost-btn"
              className="bg-white text-danger font-bold py-3 px-6 rounded-full border-2 border-white hover:bg-white/90 transition-all shadow-brutal flex items-center gap-2 text-sm shrink-0"
            >
              <Siren className="w-4 h-4" />
              Declarer un objet perdu
            </Link>
          )}
        </div>
      </div>

      <main className="px-4 sm:px-8 lg:px-12 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="w-4 h-4 text-charcoal/50" />
              <button
                onClick={() => setFilter('all')}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all ${filter === 'all' ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal/70 border-charcoal/20 hover:border-charcoal/50'}`}
                data-testid="filter-all"
              >
                {t('lost.allCategories')} ({items.length})
              </button>
              {Object.entries(allCategories).map(([key, label]) => {
                const count = items.filter(i => i.category === key).length;
                if (count === 0) return null;
                return (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-all ${filter === key ? 'bg-charcoal text-white border-charcoal' : 'bg-white text-charcoal/70 border-charcoal/20 hover:border-charcoal/50'}`}
                    data-testid={`filter-${key}`}
                  >
                    {label} ({count})
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1 bg-surface-alt rounded-full p-1 border border-charcoal/10">
              <button
                onClick={() => setView('map')}
                className={`text-xs font-bold px-4 py-1.5 rounded-full transition-all ${view === 'map' ? 'bg-charcoal text-white' : 'text-charcoal/60'}`}
                data-testid="view-map"
              >
                {t('lost.mapView')}
              </button>
              <button
                onClick={() => setView('list')}
                className={`text-xs font-bold px-4 py-1.5 rounded-full transition-all ${view === 'list' ? 'bg-charcoal text-white' : 'text-charcoal/60'}`}
                data-testid="view-list"
              >
                {t('lost.listView')}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-danger border-t-transparent" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="card-brutal text-center py-16">
              <div className="bg-green-100 w-20 h-20 rounded-full border-2 border-charcoal flex items-center justify-center mx-auto mb-6">
                <Package className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">{t('lost.noLostObjects')}</h3>
              <p className="text-charcoal/60">{t('lost.noLostObjectsDesc')}</p>
            </div>
          ) : (
            <>
              {/* Map View */}
              {view === 'map' && (
                <div className="border-2 border-charcoal rounded-2xl shadow-brutal overflow-hidden mb-8" data-testid="lost-map" style={{ height: '500px' }}>
                  {mappableItems.length > 0 ? (
                    <MapContainer center={mapCenter} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {mappableItems.map((item) => (
                        <Marker
                          key={item.pingme_id}
                          position={[item.last_scan_location.latitude, item.last_scan_location.longitude]}
                          icon={item.urgent_lost ? urgentIcon : new L.Icon.Default()}
                        >
                          <Popup>
                            <div className="min-w-[180px]">
                              {item.photo_url && (
                                <img
                                  src={item.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${item.photo_url}` : item.photo_url}
                                  alt={item.name}
                                  style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px', border: '2px solid #1a1a1a' }}
                                />
                              )}
                              <p className="font-bold text-sm">{item.name}</p>
                              <p className="text-xs text-gray-500">{categoryLabel(item.category) || item.category}</p>
                              {item.urgent_lost && <p className="text-xs text-red-600 font-bold mt-1">{t('lost.urgent')}</p>}
                              {item.urgent_details?.last_location && <p className="text-xs mt-1">{item.urgent_details.last_location}</p>}
                              {item.reward_amount && <p className="text-xs text-green-600 font-bold mt-1">{t('lost.reward')}: {item.reward_amount} EUR</p>}
                              <a href={`/ping/${item.pingme_id}`} className="text-xs text-blue-600 underline mt-2 block">{t('lost.contactOwner')}</a>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-surface-alt">
                      <div className="text-center">
                        <MapPin className="w-12 h-12 text-charcoal/30 mx-auto mb-3" />
                        <p className="text-charcoal/50 text-sm">Aucun objet avec localisation disponible</p>
                        <p className="text-charcoal/40 text-xs mt-1">Les positions sont enregistrees lors des scans QR</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* List View */}
              {view === 'list' && (
                <div className="space-y-4" data-testid="lost-list">
                  {filteredItems.map((item) => {
                    const Icon = categoryIcons[item.category] || Package;
                    return (
                      <Link
                        key={item.pingme_id}
                        to={`/ping/${item.pingme_id}`}
                        data-testid={`lost-item-${item.pingme_id}`}
                        className="block card-brutal hover:shadow-brutal-xl hover:-translate-y-0.5 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          {item.photo_url ? (
                            <img
                              src={item.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${item.photo_url}` : item.photo_url}
                              alt={item.name}
                              className="w-16 h-16 rounded-xl border-2 border-charcoal object-cover shrink-0"
                            />
                          ) : (
                            <div className={`w-16 h-16 rounded-xl border-2 border-charcoal flex items-center justify-center shrink-0 ${item.urgent_lost ? 'bg-danger' : 'bg-danger/20'}`}>
                              <Icon className={`w-8 h-8 ${item.urgent_lost ? 'text-white' : 'text-charcoal'}`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-bold text-sm truncate">{item.name}</h3>
                              {item.urgent_lost && (
                                <span className="text-xs font-bold text-white bg-danger px-2 py-0.5 rounded-full shrink-0 animate-pulse">URGENT</span>
                              )}
                            </div>
                            <p className="text-xs text-charcoal/60">{categoryLabel(item.category) || item.category}</p>
                            {item.urgent_details?.last_location && (
                              <p className="text-xs text-charcoal/50 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {item.urgent_details.last_location}</p>
                            )}
                            {item.urgent_details?.description && (
                              <p className="text-xs text-charcoal/40 italic mt-0.5">"{item.urgent_details.description}"</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            {item.reward_amount && (
                              <div className="flex items-center gap-1 text-xs font-bold text-green-600 mb-1">
                                <Gift className="w-3 h-3" /> {item.reward_amount} EUR
                              </div>
                            )}
                            <p className="text-xs text-charcoal/40">
                              {item.urgent_details?.declared_at ? timeSince(item.urgent_details.declared_at) : timeSince(item.created_at)}
                            </p>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Always show list below map */}
              {view === 'map' && filteredItems.length > 0 && (
                <div>
                  <h3 className="font-heading font-bold text-lg mb-4">{filteredItems.length} objet(s) perdu(s)</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredItems.map((item) => {
                      const Icon = categoryIcons[item.category] || Package;
                      return (
                        <Link
                          key={item.pingme_id}
                          to={`/ping/${item.pingme_id}`}
                          data-testid={`lost-card-${item.pingme_id}`}
                          className="card-brutal hover:shadow-brutal-xl hover:-translate-y-0.5 transition-all flex items-center gap-3"
                        >
                          {item.photo_url ? (
                            <img
                              src={item.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${item.photo_url}` : item.photo_url}
                              alt={item.name}
                              className="w-12 h-12 rounded-lg border-2 border-charcoal object-cover shrink-0"
                            />
                          ) : (
                            <div className={`w-12 h-12 rounded-lg border-2 border-charcoal flex items-center justify-center shrink-0 ${item.urgent_lost ? 'bg-danger' : 'bg-danger/20'}`}>
                              <Icon className={`w-6 h-6 ${item.urgent_lost ? 'text-white' : 'text-charcoal'}`} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{item.name}</p>
                            <p className="text-xs text-charcoal/50">{categoryLabel(item.category) || item.category}</p>
                          </div>
                          {item.reward_amount && (
                            <span className="text-xs font-bold text-green-600 shrink-0">{item.reward_amount} EUR</span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default LostObjects;
